/* eslint-disable no-underscore-dangle */

/*
	This mixin is used to set different options of both Bar Chart and Line Chart

	These are not general enough to be included in other charts,
	but are used in both bar charts and line charts.
*/
import chartMixin from './mixins/chart';

export default {
	mixins: [
		chartMixin,
	],

	render(createElement) {
		let longDescription = null;

		if (this.useLongDescription) {
			longDescription = createElement(
				'div', {
					class: 'chart__long-description screen-reader-text',
					attrs: {
						id: `long-description-${this._uid}`,
					},
					domProps: {
						innerHTML: this.longDescription,
					},
				},
			);
		}

		return createElement(
			'div', {
				class: 'chart',
				attrs: {
					id: 'chart',
				},
				style: {
					height: this.chartHeight,
					width: this.chartWidth,
				},
			},
			[
				createElement(
					'div', {
						class: 'chart__graph',
						style: {
							position: 'relative',
							height: '100%',
							width: '100%',
						},
					}, [
						createElement(
							'canvas', {
								class: 'chart__canvas',
								attrs: {
									id: this.chartId,
									'aria-label': `${this.computedAriaLabel}`,
									'aria-describedby': `long-description-${this._uid}`,
									role: 'img',
								},
								ref: 'canvas',
							},
						),
						createElement(
							'div', {
								class: 'chart__tooltip-anchor',
								attrs: {
									'data-anchor': `anchor-${this.chartId}-${this._uid}`,
								},
								style: {
									position: 'absolute',
									top: this.tooltipAnchorY,
									left: this.tooltipAnchorX,
								},
								ref: `anchor-${this.chartId}-${this._uid}`,
							},
						),
						createElement(
							'helio-tooltip', {
								class: 'chart__tooltip',
								props: {
									anchor: `anchor-${this.chartId}-${this._uid}`,
									active: !!this.tooltipHtml,
									gap: 18,
									position: this.horizontal || this.chartData.datasets.length > 1 ? 'right' : 'top',
									portal: true,
									skin: this.theme === 'light' ? 'dark' : 'light',
								},
								attrs: {
									class: 'tooltip--chart',
								},
								ref: `tooltip-${this.chartId}-${this._uid}`,
							}, [
								createElement(
									'template', {
										slot: 'content',
									}, [
										createElement(
											'span', {
												domProps: {
													innerHTML: this.tooltipHtml,
												},
											},
										),
									],
								),
							],
						),
					],
				),
				// long description for screen reader
				longDescription,
			],
		);
	},

	props: {
		ariaLabel: {
			type: String,
			required: false,
			default: null,
			description: 'Supplies an aria-label for the chart as a whole. If this prop isn\'t passed, a default aria-label of "graphique de données" / "data graph" will be used.',
		},

		horizontal: {
			type: Boolean,
			required: false,
			default: false,
			description: 'If set to true, bars will be horizontal',
		},

		lineLabel: {
			type: [Array, String],
			required: false,
			default: '',
			description: 'Supplies the label for each line',
		},

		showGridLines: {
			type: String,
			required: false,
			default: 'both',
			validator: value => [
				'both',
				'y-only',
				'x-only',
				'none',
			].indexOf(value) !== -1,
			description: 'Sets whether the y and x grid lines will be shown',
		},

		showLegend: {
			type: Boolean,
			required: false,
			default: true,
			description: 'When there are more than two datasets, the legend will be displayed by default. Set to false to disable this behavior.',
		},

		xAxisLabel: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the label of the graph X label',
		},

		xAxisUnits: {
			type: String,
			required: false,
			default: '',
			description: 'Provides information about the unit applied to the y-axis intervals when relevant. This information is important for the autogeneration of meaningful long descriptions.',
		},

		yAxisLabel: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the label of the graph Y label',
		},

		yAxisUnits: {
			type: String,
			required: false,
			default: '',
			description: 'Provides information about the unit applied to the y-axis intervals when relevant. This information is important for the autogeneration of meaningful long descriptions.',
		},
	},

	data() {
		return {
			longDescription: '',
		};
	},

	watch: {
		locale() {
			this.generateLongDescription();
		},
	},

	mounted() {
		this.generateLongDescription();
	},

	computed: {
		cartesianOptions() {
			let legend = {
				display: false,
			};

			if (
				this.showLegend
				&& this.lineLabel
				&& this.lineLabel.length === this.chartData.datasets.length
			) {
				legend = {
					display: true,
					labels: {
						boxWidth: 8,
						color: this.style[`${this.theme}-axis-label`],
						padding: 30,
						usePointStyle: true,
					},
					position: 'bottom',
				};
			}

			return {
				indexAxis: this.horizontal ? 'y' : 'x',
				borderRadius: 50,
				scales: {
					x: {
						grid: {
							borderColor: this.style[`${this.theme}-grid-zero`],
							color: this.style[`${this.theme}-grid`],
							display: this.gridDisplay
								? this.gridDisplay.x
								: true,
							drawTicks: !this.horizontal,
						},
						title: {
							display: !!this.xAxisLabel,
							color: this.style[`${this.theme}-axis-label`],
							font: {
								size: '10px',
							},
							letterSpacing: '0.45px',
							lineHeight: '28px',
							textAlign: 'center',
							padding: {
								top: 20,
								left: 0,
								right: 0,
								bottom: 0,
							},
							text: this.xAxisLabel
								? this.xAxisLabel.toUpperCase()
								: '',
						},
						ticks: {
							color: this.style[`${this.theme}-font-color`],
							font: {
								size: '12px',
							},
						},
					},
					y: {
						grid: {
							borderColor: this.style[`${this.theme}-grid-zero`],
							color: this.style[`${this.theme}-grid`],
							display: this.gridDisplay
								? this.gridDisplay.y
								: true,
							drawTicks: !this.horizontal,
						},
						title: {
							display: !!this.yAxisLabel,
							color: this.style[`${this.theme}-axis-label`],
							font: {
								size: '10px',
							},
							letterSpacing: '0.45px',
							lineHeight: '28px',
							textAlign: 'center',
							padding: {
								top: 0,
								left: 0,
								right: 0,
								bottom: 18,
							},
							text: this.yAxisLabel
								? this.yAxisLabel.toUpperCase()
								: '',
						},
						ticks: {
							color: this.style[`${this.theme}-font-color`],
							font: {
								size: '12px',
							},
						},
					},
				},
				plugins: {
					legend,
					tooltip: {
						enabled: false,
						external: this.customTooltips,
						intersect: false,
						mode: 'index',
					},
				},
				interaction: {
					mode: 'index',
					intersect: false,
				},
				maintainAspectRatio: false,
			};
		},

		computedAriaLabel() {
			const backupAriaLabel = this.$t('cartesian-chart.aria-label');
			return this.ariaLabel || backupAriaLabel;
		},

		gridDisplay() {
			const gridDisplay = {
				x: true,
				y: true,
			};

			if (this.showGridLines === 'y-only') {
				gridDisplay.x = false;
			} else if (this.showGridLines === 'x-only') {
				gridDisplay.y = false;
			} else if (this.showGridLines === 'none') {
				gridDisplay.x = false;
				gridDisplay.y = false;
			}

			return gridDisplay;
		},

		locale() {
			return this.$i18n.locale;
		},
	},

	methods: {
		customTooltips(tooltipModel) {
			this.tooltipHtml = null;

			// Hide if no tooltip
			if (tooltipModel.tooltip.opacity === 0) {
				return;
			}

			this.tooltipAnchorY = `${tooltipModel.tooltip.caretY}px`;
			this.tooltipAnchorX = `${tooltipModel.tooltip.caretX}px`;

			const getBody = (bodyItem) => bodyItem.lines;

			// Set text
			if (tooltipModel.tooltip.body) {
				const titleLines = tooltipModel.tooltip.title || [];
				const bodyLines = tooltipModel.tooltip.body.map(getBody);

				let innerHtml = '';

				titleLines.forEach(title => {
					innerHtml += this.tooltipHasLabel
						? `<span class="tooltip__title">${title}</span>`
						: '';
				});

				bodyLines.forEach((body, i) => {
					// get body text
					const bodyWithoutLabel = body[0].split(': ').pop();
					const bodyText = this.tooltipHasLabel ? body : bodyWithoutLabel;

					// get colors
					const colors = tooltipModel.tooltip.labelColors[i];

					let bodyStyle = `border-color: ${colors.borderColor};`;

					if (
						colors.backgroundColor instanceof CanvasGradient
						|| colors.backgroundColor === 'transparent'
					) {
						bodyStyle += `background: ${colors.borderColor};`;
					} else {
						bodyStyle += `background: ${colors.backgroundColor};`;
					}

					// get html
					const span = `<span class="tooltip__label-icon" style="${bodyStyle}"></span>`;
					const data = this.tooltipUnitPrefix + bodyText + this.tooltipUnitSuffix;

					innerHtml += this.tooltipHasColor
						? `<span class="tooltip__label">${span}${data}</span>`
						: `<span class="tooltip__label">${data}</span>`;
				});

				this.$nextTick(() => {
					this.tooltipHtml = innerHtml;
				});
			}
		},

		generateLongDescription() {
			// **get title**
			const title = (this.xAxisLabel && this.yAxisLabel)
				? this.$t('cartesian-chart.title-with-units', {
					xAxisLabel: this.xAxisLabel,
					yAxisLabel: this.yAxisLabel,
				})
				: this.$t('cartesian-chart.simple-title');

			// **get description**
			let description = '';

			// check depth of chartData array
			const data = [...this.chartData.datasets];
			const dataDepth = this.getArrayDepth(data);

			if (data) {
				if ((dataDepth === 1) || (dataDepth > 1 && data.length === 1)) {
					// if there's a single dataset, create textual description
					let textData = null;
					if (dataDepth === 1) {
						textData = data;
					} else if (dataDepth > 1 && data.length === 1) {
						textData = data[0];
					}

					const descriptionArr = [];
					textData.forEach(entry => descriptionArr.push(`<li>${this.$t('cartesian-chart.text-description', {
						xAxisValue: entry.datasetLabel,
						xAxisUnit: this.xAxisUnits,
						yAxisValue: entry.data,
						yAxisUnit: this.yAxisUnits,
					})}</li>`));
					description = descriptionArr.join(' ');

					this.longDescription = `<p class="chart__long-description">${title} <ul>${description}</ul></p>`;
				} else if (dataDepth > 1 && data.length > 1) {
					// if there are multiple datasets, create table

					// get data for assembly of table
					const graphLabels = ['', ...this.lineLabel];
					const xAxisLabels = data[0].map(item => item.datasetLabel);
					const datasetArrays = data.map(datum => datum.map(item => `${item.data} ${this.yAxisUnits}`));
					const graphData = [xAxisLabels, ...datasetArrays];
					const completeData = [...graphData];
					completeData.map((array, index) => [...array.splice(0, 0, graphLabels[index])]);

					// get table header
					const header = graphData[0];
					const theader = header.map(head => `<th>${head}  ${this.xAxisUnits}</th>`).join('');

					// get table body
					const body = [...completeData];
					const bodyData = [...body.splice(1, 2)];
					const keys = [...bodyData.reduce((all, obj) => {
						Object.keys(obj).forEach(key => all.add(key));
						return all;
					}, new Set())];
					const tbody = bodyData.map(row => keys.map(key => `<td>${row[key]}</td>`).join('')).map(row => `<tr>${row}</tr>`).join('');

					// set description
					description = `<table>
						<thead><tr>${theader}</tr></thead>
						<tbody>${tbody}</body>
					</table>`;

					// set long description
					this.longDescription = `<p class="chart__long-description">${title}</p>${description}`;
				}
			}
		},
	},
};
